import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";

import { getClearingEMPs } from "../../../services/clearing/emps";
import { VAT_OPTION_OPTIONS } from "../../../components/inputs/consts";

import Loader from "../../components/Loader";
import ReusableList from "../../../components/ReusableList";

interface EMP {
  id: string;
  name: string;
  email: string;
  companyName: string;
  country: string;
  vatOption: string | null;
  dueInDays: number | null;
}

const vatOptionBodyTemplate = (rowData: EMP) => {
  const option = VAT_OPTION_OPTIONS.find((o) => o.code === rowData.vatOption);
  return option?.name || rowData.vatOption || "-";
};

const columns = [
  {
    label: "Company Name",
    value: "companyName",
    sortable: true,
    style: { width: "20%" },
  },
  {
    label: "Name",
    value: "mandantName",
    sortable: true,
    style: { width: "20%" },
  },
  {
    label: "Country",
    value: "country",
    sortable: true,
    style: { width: "10%" },
  },
  {
    label: "Email",
    value: "email",
    sortable: true,
    style: { width: "25%" },
  },
  {
    label: "VAT Option",
    value: "vatOption",
    sortable: true,
    style: { width: "10%" },
    body: (rowData: EMP) => vatOptionBodyTemplate(rowData),
  },
  {
    label: "Due In Days",
    value: "dueInDays",
    sortable: true,
    style: { width: "10%" },
  },
];

const EMPsList = () => {
  const navigate = useNavigate();

  const toast = useRef<Toast>(null);

  const [emps, setEmps] = useState<EMP[]>([]);
  const [loading, setLoading] = useState<boolean | null>(true);

  const fetchEMPsData = useCallback(async () => {
    setLoading(true);

    try {
      const empsList = await getClearingEMPs();

      if (!empsList || empsList?.length === 0) {
        setEmps([]);
        return;
      }

      const formattedEMPList = empsList.map((item: any) => ({
        ...(item || {}),
        name: `${item?.firstName} ${item?.lastName}`,
        email: item?.email?.join(", ") || "",
        companyName: item?.businessAddressCompanyName,
        country: item?.businessAddressCountry,
      }));

      setEmps(formattedEMPList);
    } catch (err) {
      console.error("EMPS fetch", err);

      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: err?.messge || "Failed to fetch EMPS.",
          life: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEMPsData();
  }, [fetchEMPsData]);

  if (loading) {
    return <Loader />;
  }

  const actionButtons = (
    <div className="flex flex-column md:flex-row md:justify-content-end gap-2">
      <Button
        className="w-full md:w-12rem md:my-4"
        label="Add EMP"
        icon="pi pi-user-plus"
        outlined
        onClick={() => navigate("/customers/emps/add")}
      />

    </div>
  );

  return (
    <div className="emp-list-container">
      <Toast ref={toast} className="z-10" />
      <ReusableList
        data={emps}
        actionButtons={actionButtons}
        columns={columns}
        onRowClick={(e: any) =>
          navigate(`/customers/emps/edit`, {
            state: { details: e.data },
          })
        }
        searchable
        loading={false}
      />
    </div>
  );
};

export default EMPsList;
